import { useEffect, useState, useCallback } from "react";
import { http, errText, inr, API } from "../lib/api";

const field = "w-full min-h-[42px] rounded-xl border-2 border-[#e1e2e5] px-3 py-2 font-medium focus:border-[#004040] outline-none bg-white";
const btn = "min-h-[42px] px-4 rounded-full bg-[#191c1f] text-white font-bold";
const btnO = "min-h-[42px] px-4 rounded-full bg-[#f06400] text-[#191c1f] font-extrabold";
const card = "bg-white rounded-2xl border border-[#e1e2e5] p-5";

function Login({ onDone }) {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { const { data } = await http.post("/auth/login", { email, password: pw }); onDone(data.user); }
    catch (ex) { setErr(errText(ex?.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen grid place-items-center bg-[#f4f5f6] px-4">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-[24px] p-7 border border-[#e1e2e5]" data-testid="admin-login">
        <img src="/assets/logo.png" alt="BLC" className="h-10 mb-4" />
        <h1 className="text-2xl font-extrabold tracking-[-0.03em]">Admin login</h1>
        <label className="block text-sm font-bold mt-4 mb-1">Email</label>
        <input className={field} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email" autoComplete="username" />
        <label className="block text-sm font-bold mt-3 mb-1">Password</label>
        <input type="password" className={field} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="admin-login-password" autoComplete="current-password" />
        {err && <p className="text-sm text-[#8a2412] font-bold mt-2" data-testid="admin-login-error">{err}</p>}
        <button className={`${btn} w-full mt-5`} disabled={busy} data-testid="admin-login-submit">{busy ? "…" : "Log in"}</button>
      </form>
    </div>
  );
}

function Leads() {
  const [list, setList] = useState([]);
  const [meta, setMeta] = useState({ total: 0, today: 0 });
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const load = useCallback(async () => {
    const { data } = await http.get(`/admin/leads?q=${encodeURIComponent(q)}`);
    setList(data.leads); setMeta({ total: data.total, today: data.today });
  }, [q]);
  useEffect(() => { load(); }, [load]);
  const exportCsv = () => { window.open(`${API}/admin/leads/export`, "_blank"); };
  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input className={`${field} max-w-xs`} placeholder="Search name, mobile, ID, city" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-leads-search" />
        <button className={btn} onClick={load} data-testid="admin-leads-refresh">Search</button>
        <button className={btnO} onClick={exportCsv} data-testid="admin-export-csv-btn">Download CSV</button>
        <span className="text-sm text-[#6b6f76]">{meta.total} total · {meta.today} today</span>
      </div>
      <div className={`${card} overflow-x-auto`}>
        <table className="w-full text-sm" data-testid="admin-leads-table">
          <thead><tr className="text-left text-[#6b6f76] text-xs uppercase border-b border-[#e1e2e5]">
            <th className="py-2 pr-3">ID</th><th className="pr-3">Name</th><th className="pr-3">Mobile</th><th className="pr-3">Product</th><th className="pr-3">Source</th><th className="pr-3">Stage</th><th className="pr-3">Status</th>
          </tr></thead>
          <tbody>
            {list.map((l) => (
              <tr key={l.lead_id} className="border-b border-[#f0f1f2] hover:bg-[#f9fafb] cursor-pointer" onClick={() => setSel(l.lead_id)} data-testid={`admin-lead-row-${l.lead_id}`}>
                <td className="py-2 pr-3 font-bold text-[#004040]">{l.lead_id}</td><td className="pr-3">{l.name}</td><td className="pr-3">{l.mobile}</td>
                <td className="pr-3">{l.product_label}</td><td className="pr-3">{l.source}</td><td className="pr-3">{l.outcome ? l.outcome : (l.stage + 1)}</td>
                <td className="pr-3"><span className="text-xs px-2 py-0.5 rounded-full bg-[#fff0e6] text-[#b84a00]">{l.status}</span></td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-[#6b6f76]">No leads yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {sel && <LeadDetail leadId={sel} onClose={() => { setSel(null); load(); }} />}
    </div>
  );
}

function LeadDetail({ leadId, onClose }) {
  const [d, setD] = useState(null);
  const [form, setForm] = useState({});
  const load = useCallback(async () => {
    const { data } = await http.get(`/admin/leads/${leadId}`);
    setD(data); setForm({ stage: data.lead.stage, status: data.lead.status, outcome: data.lead.outcome || "", reason_code: data.lead.reason_code || "", notes: data.lead.notes || "" });
  }, [leadId]);
  useEffect(() => { load(); }, [load]);
  const save = async () => { await http.patch(`/admin/leads/${leadId}`, { ...form, outcome: form.outcome || null, reason_code: form.reason_code || null }); onClose(); };
  const del = async () => { if (window.confirm("Delete this lead and its consent record permanently?")) { await http.delete(`/admin/leads/${leadId}`); onClose(); } };
  if (!d) return null;
  const STAGES = ["Enquiry received", "Mobile verified", "Options matched", "Documents", "Lender review", "Sanctioned", "Disbursed"];
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div className={`${card} max-w-lg w-full max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()} data-testid="admin-lead-detail">
        <div className="flex justify-between items-center"><h3 className="text-xl font-extrabold">{d.lead.lead_id}</h3><button onClick={onClose} className="font-bold">✕</button></div>
        <div className="grid grid-cols-2 gap-2 text-sm mt-3">
          <div><span className="text-[#6b6f76]">Name</span><br />{d.lead.name}</div>
          <div><span className="text-[#6b6f76]">Mobile</span><br />{d.lead.mobile}</div>
          <div><span className="text-[#6b6f76]">Product</span><br />{d.lead.product_label}</div>
          <div><span className="text-[#6b6f76]">Amount</span><br />Rs {inr(d.lead.amount_requested)}</div>
          <div><span className="text-[#6b6f76]">Source</span><br />{d.lead.source}</div>
          <div><span className="text-[#6b6f76]">Credit range</span><br />{d.lead.credit_score_range || "-"}</div>
        </div>
        <label className="block text-sm font-bold mt-4 mb-1">Stage shown to customer</label>
        <select className={field} value={form.stage} onChange={(e) => setForm((f) => ({ ...f, stage: +e.target.value }))} data-testid="admin-lead-stage">
          {STAGES.map((s, i) => <option key={i} value={i}>{i + 1}. {s}</option>)}
        </select>
        <div className="grid grid-cols-2 gap-2 mt-3">
          <div><label className="block text-sm font-bold mb-1">Status</label>
            <select className={field} value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
              {["open", "closed", "lost"].map((s) => <option key={s}>{s}</option>)}
            </select></div>
          <div><label className="block text-sm font-bold mb-1">Outcome</label>
            <select className={field} value={form.outcome} onChange={(e) => setForm((f) => ({ ...f, outcome: e.target.value }))} data-testid="admin-lead-outcome">
              <option value="">In progress</option><option value="not_matched">Not matched yet</option><option value="declined">Declined by lender</option>
            </select></div>
        </div>
        {form.outcome && (
          <><label className="block text-sm font-bold mt-3 mb-1">Reason code</label>
          <select className={field} value={form.reason_code} onChange={(e) => setForm((f) => ({ ...f, reason_code: e.target.value }))}>
            <option value="">—</option>{["low_credit_score", "income", "documents", "location", "other"].map((r) => <option key={r} value={r}>{r.replace(/_/g, " ")}</option>)}
          </select></>
        )}
        <label className="block text-sm font-bold mt-3 mb-1">Internal notes</label>
        <textarea className={`${field} min-h-[90px]`} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid="admin-lead-notes" />
        <div className="mt-3 text-xs text-[#6b6f76]">
          Consent: {d.consent ? `${d.consent.version} · ${d.consent.channels} · ${d.consent.ts?.slice(0, 16)} · IP ${d.consent.ip}` : "none on file"}
        </div>
        <div className="flex gap-2 mt-4">
          <button className={btnO} onClick={save} data-testid="admin-lead-save">Save</button>
          <button className="min-h-[42px] px-4 rounded-full bg-[#c0301b] text-white font-bold" onClick={del} data-testid="admin-lead-delete">Delete (erasure)</button>
        </div>
      </div>
    </div>
  );
}

function Posts() {
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = async () => { const { data } = await http.get("/admin/posts"); setList(data.posts); };
  useEffect(() => { load(); }, []);
  const blank = { kind: "blog", title: "", category: "", excerpt: "", body_md: "", read_min: 3, published: true, pub_date: "" };
  const save = async () => {
    if (edit.post_id) await http.patch(`/admin/posts/${edit.post_id}`, edit);
    else await http.post("/admin/posts", edit);
    setEdit(null); load();
  };
  const del = async (id) => { if (window.confirm("Delete post?")) { await http.delete(`/admin/posts/${id}`); load(); } };
  return (
    <div>
      <button className={`${btnO} mb-4`} onClick={() => setEdit(blank)} data-testid="admin-new-post">+ New post</button>
      <div className={`${card} overflow-x-auto`}>
        <table className="w-full text-sm"><thead><tr className="text-left text-[#6b6f76] text-xs uppercase border-b border-[#e1e2e5]"><th className="py-2 pr-3">Title</th><th className="pr-3">Type</th><th className="pr-3">Date</th><th className="pr-3">Status</th><th></th></tr></thead>
          <tbody>{list.map((p) => (
            <tr key={p.post_id} className="border-b border-[#f0f1f2]">
              <td className="py-2 pr-3 font-bold">{p.title}</td><td className="pr-3">{p.kind}</td><td className="pr-3">{p.pub_date}</td>
              <td className="pr-3">{p.published ? <span className="text-[#004040] font-bold">Live</span> : <span className="text-[#6b6f76]">Draft</span>}</td>
              <td className="pr-3"><button className="underline font-bold" onClick={() => setEdit(p)} data-testid={`admin-edit-post-${p.post_id}`}>Edit</button> · <button className="underline text-[#c0301b]" onClick={() => del(p.post_id)}>Delete</button></td>
            </tr>
          ))}</tbody></table>
      </div>
      {edit && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setEdit(null)}>
          <div className={`${card} max-w-xl w-full max-h-[90vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()} data-testid="admin-post-editor">
            <h3 className="text-xl font-extrabold">{edit.post_id ? "Edit" : "New"} post</h3>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <div><label className="text-sm font-bold">Type</label>
                <select className={field} value={edit.kind} onChange={(e) => setEdit({ ...edit, kind: e.target.value })}><option value="blog">Blog</option><option value="announcement">Announcement</option></select></div>
              <div><label className="text-sm font-bold">Date</label><input type="date" className={field} value={edit.pub_date} onChange={(e) => setEdit({ ...edit, pub_date: e.target.value })} /></div>
            </div>
            <label className="text-sm font-bold mt-3 block">Title</label><input className={field} value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} data-testid="admin-post-title" />
            <label className="text-sm font-bold mt-3 block">Category label</label><input className={field} value={edit.category} onChange={(e) => setEdit({ ...edit, category: e.target.value })} />
            <label className="text-sm font-bold mt-3 block">Summary / excerpt</label><textarea className={`${field} min-h-[60px]`} value={edit.excerpt} onChange={(e) => setEdit({ ...edit, excerpt: e.target.value })} />
            <label className="text-sm font-bold mt-3 block">Body (blank line=paragraph, "## "=heading, "- "=bullet, **bold**, [text](https://link))</label>
            <textarea className={`${field} min-h-[180px] font-mono text-xs`} value={edit.body_md} onChange={(e) => setEdit({ ...edit, body_md: e.target.value })} data-testid="admin-post-body" />
            <label className="flex items-center gap-2 mt-3 font-bold"><input type="checkbox" className="w-5 h-5 accent-[#f06400]" checked={edit.published} onChange={(e) => setEdit({ ...edit, published: e.target.checked })} data-testid="admin-post-published" /> Show on website</label>
            <div className="flex gap-2 mt-4"><button className={btnO} onClick={save} data-testid="admin-post-save">Save</button><button className="min-h-[42px] px-4 rounded-full bg-[#e9eaec] font-bold" onClick={() => setEdit(null)}>Cancel</button></div>
          </div>
        </div>
      )}
    </div>
  );
}

function Offers() {
  const [o, setO] = useState(null);
  useEffect(() => { http.get("/admin/offers").then(({ data }) => setO(data.offer)); }, []);
  const save = async () => { await http.patch("/admin/offers", o); alert("Saved."); };
  if (!o) return null;
  return (
    <div className={`${card} max-w-xl`} data-testid="admin-offers">
      <h3 className="text-xl font-extrabold">Diwali offer &amp; flash bar</h3>
      <label className="flex items-center gap-2 mt-3 font-bold"><input type="checkbox" className="w-5 h-5 accent-[#f06400]" checked={o.enabled} onChange={(e) => setO({ ...o, enabled: e.target.checked })} data-testid="admin-offer-enabled" /> Enabled</label>
      <label className="text-sm font-bold mt-3 block">Ticker text</label><textarea className={`${field} min-h-[80px]`} value={o.ticker} onChange={(e) => setO({ ...o, ticker: e.target.value })} data-testid="admin-offer-ticker" />
      <label className="text-sm font-bold mt-3 block">Image URL</label><input className={field} value={o.image} onChange={(e) => setO({ ...o, image: e.target.value })} />
      <div className="grid grid-cols-2 gap-2 mt-3">
        <div><label className="text-sm font-bold">Start (ISO)</label><input className={field} value={o.start} onChange={(e) => setO({ ...o, start: e.target.value })} /></div>
        <div><label className="text-sm font-bold">End (ISO)</label><input className={field} value={o.end} onChange={(e) => setO({ ...o, end: e.target.value })} data-testid="admin-offer-end" /></div>
      </div>
      <button className={`${btnO} mt-4`} onClick={save} data-testid="admin-offer-save">Save</button>
    </div>
  );
}

function RatesPlans() {
  const [s, setS] = useState(null);
  useEffect(() => { http.get("/admin/settings").then(({ data }) => setS(data.settings)); }, []);
  const save = async () => { await http.patch("/admin/settings", { rates: s.rates, rate_basis: s.rate_basis, plans: s.plans, tax_note: s.tax_note, opc_name: s.opc_name, opc_cin: s.opc_cin, flags: s.flags }); alert("Saved."); };
  if (!s) return null;
  return (
    <div className="grid gap-5" data-testid="admin-rates-plans">
      <div className={`${card} max-w-2xl`}>
        <h3 className="text-xl font-extrabold">Interest rates</h3>
        <label className="text-sm font-bold mt-3 block">Rate basis</label>
        <select className={`${field} max-w-xs`} value={s.rate_basis} onChange={(e) => setS({ ...s, rate_basis: e.target.value })} data-testid="admin-rate-basis">
          {["unspecified", "reducing", "flat"].map((x) => <option key={x}>{x}</option>)}
        </select>
        <div className="mt-3 space-y-2">
          {s.rates.map((r, i) => (
            <div key={i} className="grid grid-cols-2 gap-2">
              <input className={field} value={r.loan} onChange={(e) => { const n = [...s.rates]; n[i] = { ...r, loan: e.target.value }; setS({ ...s, rates: n }); }} />
              <input className={field} value={r.rate} onChange={(e) => { const n = [...s.rates]; n[i] = { ...r, rate: e.target.value }; setS({ ...s, rates: n }); }} />
            </div>
          ))}
        </div>
      </div>
      <div className={`${card} max-w-2xl`}>
        <h3 className="text-xl font-extrabold">Plans</h3>
        {s.plans.map((p, i) => (
          <div key={p.id} className="grid grid-cols-4 gap-2 mt-2 items-center">
            <span className="font-bold">{p.label}</span>
            <input className={field} type="number" value={p.price} onChange={(e) => { const n = [...s.plans]; n[i] = { ...p, price: +e.target.value }; setS({ ...s, plans: n }); }} />
            <input className={field} type="number" value={p.minutes} onChange={(e) => { const n = [...s.plans]; n[i] = { ...p, minutes: +e.target.value }; setS({ ...s, plans: n }); }} />
            <input className={field} value={p.per_minute} onChange={(e) => { const n = [...s.plans]; n[i] = { ...p, per_minute: e.target.value }; setS({ ...s, plans: n }); }} />
          </div>
        ))}
        <label className="text-sm font-bold mt-3 block">Tax note (plan fees)</label><input className={field} value={s.tax_note} onChange={(e) => setS({ ...s, tax_note: e.target.value })} />
      </div>
      <div className={`${card} max-w-2xl`}>
        <h3 className="text-xl font-extrabold">Flags &amp; OPC placeholder</h3>
        <label className="flex items-center gap-2 mt-3 font-bold"><input type="checkbox" className="w-5 h-5 accent-[#f06400]" checked={s.flags.show_insurance} onChange={(e) => setS({ ...s, flags: { ...s.flags, show_insurance: e.target.checked } })} data-testid="admin-flag-insurance" /> Show insurance</label>
        <label className="flex items-center gap-2 mt-2 font-bold"><input type="checkbox" className="w-5 h-5 accent-[#f06400]" checked={s.flags.otp_enabled} onChange={(e) => setS({ ...s, flags: { ...s.flags, otp_enabled: e.target.checked } })} /> OTP enabled</label>
        <div className="grid grid-cols-2 gap-2 mt-3">
          <div><label className="text-sm font-bold">OPC name</label><input className={field} value={s.opc_name} onChange={(e) => setS({ ...s, opc_name: e.target.value })} placeholder="Pending" /></div>
          <div><label className="text-sm font-bold">OPC CIN</label><input className={field} value={s.opc_cin} onChange={(e) => setS({ ...s, opc_cin: e.target.value })} placeholder="Pending" /></div>
        </div>
      </div>
      <button className={`${btnO} w-fit`} onClick={save} data-testid="admin-settings-save">Save settings</button>
    </div>
  );
}

function Funnel() {
  const [f, setF] = useState(null);
  useEffect(() => { http.get("/admin/funnel").then(({ data }) => setF(data)); }, []);
  if (!f) return null;
  const tbl = (title, rows, cols) => (
    <div className={`${card} overflow-x-auto`}>
      <h3 className="font-extrabold mb-2">{title}</h3>
      <table className="w-full text-sm"><tbody>
        {rows.map((r, i) => <tr key={i} className="border-b border-[#f0f1f2]">{cols.map((c, j) => <td key={j} className={`py-1.5 ${j === 0 ? "font-bold" : "text-right"}`}>{r[c]}</td>)}</tr>)}
        {rows.length === 0 && <tr><td className="py-3 text-[#6b6f76]">No data yet.</td></tr>}
      </tbody></table>
    </div>
  );
  return (
    <div className="grid md:grid-cols-2 gap-5" data-testid="admin-funnel">
      {tbl("Events (30 days)", f.events, ["name", "count", "sessions"])}
      {tbl("Leads by product", f.by_product, ["label", "count"])}
      {tbl("Leads by stage", f.by_stage, ["stage", "count"])}
      {tbl("Plan sales", f.plan_sales, ["label", "count", "revenue"])}
    </div>
  );
}

function Orders({ role }) {
  const [list, setList] = useState([]);
  const [log, setLog] = useState(null);
  const load = async () => { const { data } = await http.get("/admin/orders"); setList(data.orders); };
  useEffect(() => { load(); }, []);
  const submitLog = async () => {
    await http.post("/consultant/sessions", { order_code: log.order_code, minutes: +log.minutes, notes: log.notes, outcome: log.outcome });
    setLog(null); load();
  };
  return (
    <div>
      <div className={`${card} overflow-x-auto`}>
        <table className="w-full text-sm" data-testid="admin-orders-table"><thead><tr className="text-left text-[#6b6f76] text-xs uppercase border-b border-[#e1e2e5]">
          <th className="py-2 pr-3">Order</th><th className="pr-3">Plan</th><th className="pr-3">Name</th><th className="pr-3">Mobile</th><th className="pr-3">Status</th><th className="pr-3">Min used/total</th><th></th></tr></thead>
          <tbody>{list.map((o) => (
            <tr key={o.order_code} className="border-b border-[#f0f1f2]">
              <td className="py-2 pr-3 font-bold text-[#004040]">{o.order_code}</td><td className="pr-3">{o.plan_label}</td><td className="pr-3">{o.name}</td><td className="pr-3">{o.mobile}</td>
              <td className="pr-3">{o.status}</td><td className="pr-3">{o.minutes_used}/{o.minutes_total}</td>
              <td className="pr-3"><button className="underline font-bold" onClick={() => setLog({ order_code: o.order_code, minutes: 10, notes: "", outcome: "" })} data-testid={`admin-log-session-${o.order_code}`}>Log session</button></td>
            </tr>
          ))}{list.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-[#6b6f76]">No orders yet.</td></tr>}</tbody></table>
      </div>
      {log && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setLog(null)}>
          <div className={`${card} max-w-sm w-full`} onClick={(e) => e.stopPropagation()} data-testid="admin-session-log">
            <h3 className="text-xl font-extrabold">Log a session — {log.order_code}</h3>
            <label className="text-sm font-bold mt-3 block">Minutes used</label><input type="number" className={field} value={log.minutes} onChange={(e) => setLog({ ...log, minutes: e.target.value })} data-testid="admin-session-minutes" />
            <label className="text-sm font-bold mt-3 block">Notes</label><textarea className={`${field} min-h-[70px]`} value={log.notes} onChange={(e) => setLog({ ...log, notes: e.target.value })} />
            <label className="text-sm font-bold mt-3 block">Outcome</label>
            <select className={field} value={log.outcome} onChange={(e) => setLog({ ...log, outcome: e.target.value })}>
              <option value="">—</option><option value="loan_ready">Loan-ready</option><option value="needs_more_time">Needs more time</option><option value="referred_module1">Referred to loan facilitation</option>
            </select>
            <button className={`${btnO} mt-4`} onClick={submitLog} data-testid="admin-session-save">Save session</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Users() {
  const [list, setList] = useState([]);
  const [nu, setNu] = useState({ email: "", password: "", name: "", role: "consultant" });
  const load = async () => { const { data } = await http.get("/admin/users"); setList(data.users); };
  useEffect(() => { load(); }, []);
  const add = async () => { try { await http.post("/admin/users", nu); setNu({ email: "", password: "", name: "", role: "consultant" }); load(); } catch (e) { alert(errText(e?.response?.data?.detail)); } };
  return (
    <div className="grid md:grid-cols-2 gap-5" data-testid="admin-users">
      <div className={card}>
        <h3 className="font-extrabold mb-2">Team</h3>
        <ul className="text-sm space-y-1">{list.map((u) => <li key={u.email} className="flex justify-between border-b border-[#f0f1f2] py-1"><span>{u.name} · {u.email}</span><span className="font-bold text-[#004040]">{u.role}</span></li>)}</ul>
      </div>
      <div className={card}>
        <h3 className="font-extrabold mb-2">Add user</h3>
        <input className={`${field} mt-1`} placeholder="Name" value={nu.name} onChange={(e) => setNu({ ...nu, name: e.target.value })} />
        <input className={`${field} mt-2`} placeholder="Email" value={nu.email} onChange={(e) => setNu({ ...nu, email: e.target.value })} data-testid="admin-newuser-email" />
        <input className={`${field} mt-2`} type="password" placeholder="Password" value={nu.password} onChange={(e) => setNu({ ...nu, password: e.target.value })} />
        <select className={`${field} mt-2`} value={nu.role} onChange={(e) => setNu({ ...nu, role: e.target.value })}><option value="consultant">Consultant</option><option value="admin">Admin</option></select>
        <button className={`${btnO} mt-3`} onClick={add} data-testid="admin-newuser-add">Add</button>
      </div>
    </div>
  );
}

function ChangePassword({ onClose }) {
  const [f, setF] = useState({ current_password: "", new_password: "", confirm: "" });
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (f.new_password !== f.confirm) { setErr("New passwords do not match."); return; }
    try { await http.post("/auth/change-password", { current_password: f.current_password, new_password: f.new_password }); setOk(true); }
    catch (ex) { setErr(errText(ex?.response?.data?.detail)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <form onSubmit={submit} className={`${card} max-w-sm w-full`} onClick={(e) => e.stopPropagation()} data-testid="admin-change-password">
        <div className="flex justify-between items-center"><h3 className="text-xl font-extrabold">Change password</h3><button type="button" onClick={onClose} className="font-bold">✕</button></div>
        {ok ? (
          <p className="mt-4 font-bold text-[#004040]" data-testid="change-password-success">Password updated. Use it the next time you log in.</p>
        ) : (
          <>
            <label className="block text-sm font-bold mt-4 mb-1">Current password</label>
            <input type="password" className={field} value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} data-testid="change-password-current" autoComplete="current-password" />
            <label className="block text-sm font-bold mt-3 mb-1">New password</label>
            <input type="password" className={field} value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} data-testid="change-password-new" autoComplete="new-password" />
            <label className="block text-sm font-bold mt-3 mb-1">Confirm new password</label>
            <input type="password" className={field} value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} data-testid="change-password-confirm" autoComplete="new-password" />
            <p className="text-xs text-[#6b6f76] mt-2">At least 8 characters with letters and numbers.</p>
            {err && <p className="text-sm text-[#8a2412] font-bold mt-2" data-testid="change-password-error">{err}</p>}
            <button className={`${btnO} w-full mt-4`} data-testid="change-password-submit">Update password</button>
          </>
        )}
      </form>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("leads");
  const [pwOpen, setPwOpen] = useState(false);
  useEffect(() => { http.get("/auth/me").then(({ data }) => setUser(data)).catch(() => setUser(null)); }, []);
  const logout = async () => { await http.post("/auth/logout"); setUser(null); };
  if (user === undefined) return <div className="min-h-screen grid place-items-center text-[#6b6f76]">Loading…</div>;
  if (!user) return <Login onDone={setUser} />;

  const TABS = [["leads", "Leads"], ["orders", "Orders & plans"], ["posts", "Posts"], ["offers", "Offers & flash"], ["rates", "Rates & plans"], ["funnel", "Funnel"], ["users", "Users"]];
  const visible = user.role === "consultant" ? TABS.filter(([k]) => ["leads", "orders"].includes(k)) : TABS;

  return (
    <div className="min-h-screen bg-[#f4f5f6]">
      <header className="bg-[#191c1f] text-white">
        <div className="max-w-[1200px] mx-auto px-4 h-[60px] flex items-center gap-4 flex-wrap">
          <b className="mr-2">BLC admin</b>
          <nav className="flex flex-wrap gap-1 flex-1">
            {visible.map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
                className={`px-3 py-1.5 rounded-full text-sm font-bold ${tab === k ? "bg-[#2b2f33]" : "hover:bg-[#2b2f33]"}`}>{l}</button>
            ))}
          </nav>
          <span className="text-xs text-white/60">{user.email} ({user.role})</span>
          <button onClick={() => setPwOpen(true)} className="text-sm font-bold underline" data-testid="admin-change-password-btn">Change password</button>
          <button onClick={logout} className="text-sm font-bold underline" data-testid="admin-logout">Log out</button>
        </div>
      </header>
      {pwOpen && <ChangePassword onClose={() => setPwOpen(false)} />}
      <main className="max-w-[1200px] mx-auto px-4 py-8">
        {tab === "leads" && <Leads />}
        {tab === "orders" && <Orders role={user.role} />}
        {tab === "posts" && <Posts />}
        {tab === "offers" && <Offers />}
        {tab === "rates" && <RatesPlans />}
        {tab === "funnel" && <Funnel />}
        {tab === "users" && <Users />}
      </main>
    </div>
  );
}
